import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { MoreVert, PictureAsPdfOutlined, PlaylistAddCheckOutlined, TableChartOutlined } from '@mui/icons-material';
import {
  BULK_ACTIONS,
  EXCEL_EXPORT,
  FIRM_PAGE_ACTIONS,
  type FirmPageAction,
  PDF_EXPORT,
} from 'common/constants/pageActions';
import { useExtraPageActionVisibility } from 'common/hooks';
import type { ExtraPageActionVisibility } from 'common/hooks';
import { PdfExportConfigDialog } from 'components/PdfExportConfigDialog';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import type { SecondaryNavigationIconButtonControlConfig } from 'context/FirmBaseLayoutContext';
import BulkActionsDrawer from 'layouts/Main/components/BulkActionsLedger/BulkActionsDrawer';
import { FirmExcelExportDialog } from 'layouts/Main/components/ExcelExportDialogV2/FirmExcelExportDialog';
import { FundExcelExportDialog } from 'layouts/Main/components/ExcelExportDialogV2/FundExcelExportDialog';
import { useUserUtils } from 'utilities/userUtils';
import { useIntelligenceContext } from './IntelligenceContext';

const MORE_VERT_ICON = <MoreVert />;

export interface UseIntelligenceExportActionsResult {
  secondaryNavigationActions: SecondaryNavigationIconButtonControlConfig[];
  exportDialogs: ReactNode;
  moreActionsMenu: ReactNode;
}

interface UseIntelligenceExportActionsOptions {
  bulkActionsEnabled?: boolean;
  initialAction?: FirmPageAction;
  onActionClose?: () => void;
}

const isActionVisible = (action: FirmPageAction, visibility: ExtraPageActionVisibility) => {
  switch (action) {
    case FIRM_PAGE_ACTIONS.BULK_ACTIONS:
      return visibility.bulkActions;
    case FIRM_PAGE_ACTIONS.PDF_EXPORT:
      return visibility.pdfExport;
    case FIRM_PAGE_ACTIONS.EXCEL_EXPORT:
    default:
      return visibility.excelExport;
  }
};

export const useIntelligenceExportActions = ({
  bulkActionsEnabled = true,
  initialAction,
  onActionClose,
}: UseIntelligenceExportActionsOptions = {}): UseIntelligenceExportActionsResult => {
  const { isAllFundsSelected, selectedFundId } = useIntelligenceContext();
  const { isStaffUser, isSuperUser } = useUserUtils();
  const actionVisibility = useExtraPageActionVisibility({
    bulkActions: bulkActionsEnabled,
    pdfExport: isStaffUser() || isSuperUser(),
  });

  const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [excelExportOpen, setExcelExportOpen] = useState(false);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const [pdfExportOpen, setPdfExportOpen] = useState(false);

  // URL-driven commands can change while Firm Valuations stays mounted, so open the matching existing action.
  useEffect(() => {
    if (!initialAction) {
      return;
    }

    if (!isActionVisible(initialAction, actionVisibility)) {
      setMoreActionsAnchorEl(null);
      setExcelExportOpen(false);
      setBulkActionsOpen(false);
      setPdfExportOpen(false);
      onActionClose?.();
      return;
    }

    setExcelExportOpen(initialAction === FIRM_PAGE_ACTIONS.EXCEL_EXPORT);
    setBulkActionsOpen(initialAction === FIRM_PAGE_ACTIONS.BULK_ACTIONS);
    setPdfExportOpen(initialAction === FIRM_PAGE_ACTIONS.PDF_EXPORT);
  }, [actionVisibility, initialAction, onActionClose]);

  const handleMoreActionsOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMoreActionsAnchorEl(event.currentTarget);
  }, []);

  const handleMoreActionsClose = useCallback(() => {
    setMoreActionsAnchorEl(null);
  }, []);

  const handleExcelExportClick = useCallback(() => {
    handleMoreActionsClose();
    setExcelExportOpen(true);
  }, [handleMoreActionsClose]);

  const handleBulkActionsClick = useCallback(() => {
    handleMoreActionsClose();
    setBulkActionsOpen(true);
  }, [handleMoreActionsClose]);

  const handleExcelExportClose = useCallback(() => {
    setExcelExportOpen(false);
    onActionClose?.();
  }, [onActionClose]);

  const handleBulkActionsClose = useCallback(() => {
    setBulkActionsOpen(false);
    onActionClose?.();
  }, [onActionClose]);

  const handlePdfExportClick = useCallback(() => {
    handleMoreActionsClose();
    setPdfExportOpen(true);
  }, [handleMoreActionsClose]);

  const handlePdfExportClose = useCallback(() => {
    setPdfExportOpen(false);
    onActionClose?.();
  }, [onActionClose]);

  const hasMoreActions = actionVisibility.excelExport || actionVisibility.bulkActions || actionVisibility.pdfExport;

  const secondaryNavigationActions = useMemo<SecondaryNavigationIconButtonControlConfig[]>(
    () =>
      hasMoreActions
        ? [
          {
            ariaLabel: 'More actions',
            icon: MORE_VERT_ICON,
            onClick: handleMoreActionsOpen,
            type: 'iconButton',
          },
        ]
        : [],
    [handleMoreActionsOpen, hasMoreActions]
  );

  const exportDialogs = (
    <>
      {!isAllFundsSelected && selectedFundId ? (
        <FundExcelExportDialog open={excelExportOpen} onClose={handleExcelExportClose} fundId={selectedFundId} />
      ) : (
        <FirmExcelExportDialog open={excelExportOpen} onClose={handleExcelExportClose} />
      )}
      <BulkActionsDrawer open={bulkActionsOpen} onClose={handleBulkActionsClose} />
      <PdfExportConfigDialog open={pdfExportOpen} onClose={handlePdfExportClose} />
    </>
  );

  const moreActionsMenu = (
    <PrimaryNavigationMenu
      anchorEl={moreActionsAnchorEl}
      menuMinWidth={0}
      menuWidth="content"
      onClose={handleMoreActionsClose}
      open={Boolean(moreActionsAnchorEl)}>
      {actionVisibility.excelExport && (
        <PrimaryNavigationMenuItem icon={<TableChartOutlined fontSize="inherit" />} onClick={handleExcelExportClick}>
          {EXCEL_EXPORT}
        </PrimaryNavigationMenuItem>
      )}
      {actionVisibility.bulkActions && (
        <PrimaryNavigationMenuItem
          icon={<PlaylistAddCheckOutlined fontSize="inherit" />}
          onClick={handleBulkActionsClick}>
          {BULK_ACTIONS}
        </PrimaryNavigationMenuItem>
      )}
      {actionVisibility.pdfExport && (
        <PrimaryNavigationMenuItem icon={<PictureAsPdfOutlined fontSize="inherit" />} onClick={handlePdfExportClick}>
          {PDF_EXPORT}
        </PrimaryNavigationMenuItem>
      )}
    </PrimaryNavigationMenu>
  );

  return {
    secondaryNavigationActions,
    exportDialogs,
    moreActionsMenu,
  };
};
